/**
 * StatCard — glass card for the profile Analytics tab.
 * Web port of client-native/src/features/profile/components/StatCard.tsx.
 *
 * borderRadius 32, padding 16, 1px white-30% border, soft shadow. A row with a
 * value (xxlarge semibold) + label (small secondary) on the left and a 44px
 * icon on the right. Either a custom-content slot below a white-20% top divider,
 * OR an action row (text + forward arrow).
 */
import { CSSProperties, ReactNode } from "react";
import { ArrowRight } from "lucide-react";
import { colors, tokens } from "@/ui";

interface StatCardBaseProps {
  value: string;
  label: string;
  icon: string;
  backgroundColor: string;
  /** Hide the divider above the custom content slot. */
  hideContentDivider?: boolean;
  /** Override icon size (default 44). */
  iconSize?: number;
}

interface StatCardWithAction extends StatCardBaseProps {
  actionText: string;
  onPress: () => void;
  children?: never;
}

interface StatCardWithChildren extends StatCardBaseProps {
  children: ReactNode;
  actionText?: never;
  onPress?: never;
}

export type StatCardProps = StatCardWithAction | StatCardWithChildren;

const glassStyle: CSSProperties = {
  position: "relative",
  borderRadius: 32,
  padding: tokens.space[4],
  border: "1px solid rgba(255,255,255,0.3)",
  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
  overflow: "hidden",
};

const highlightBorder: CSSProperties = {
  position: "absolute",
  inset: 1,
  borderRadius: 32,
  borderWidth: 2,
  borderStyle: "solid",
  borderTopColor: "rgba(255,255,255,0.3)",
  borderRightColor: "rgba(255,255,255,0.2)",
  borderBottomColor: "rgba(255,255,255,0.05)",
  borderLeftColor: "rgba(255,255,255,0.1)",
  pointerEvents: "none",
};

export function StatCard(props: StatCardProps) {
  const { value, label, icon, backgroundColor, iconSize } = props;
  const isPressable = "onPress" in props && !!props.onPress;

  const content = (
    <div style={{ ...glassStyle, backgroundColor }}>
      <div style={highlightBorder} />
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {/* textAlign:left pins the value+label to the column's left edge. Without
            it they inherit the profile page's center alignment, so a short value
            (e.g. streak "11") centers within the wider label ("days streak") and
            reads as indented/right-shifted rather than left-aligned. */}
        <div style={{ flexShrink: 1, minWidth: 0, textAlign: "left" }}>
          <div
            className="text-2xl font-semibold"
            style={{ color: colors.primary }}
          >
            {value}
          </div>
          <div className="text-sm" style={{ color: colors.secondary }}>
            {label}
          </div>
        </div>
        <img
          src={icon}
          alt=""
          style={{
            width: iconSize ?? 44,
            height: iconSize ?? 44,
            objectFit: "contain",
            flexShrink: 0,
          }}
        />
      </div>

      {"children" in props && props.children ? (
        <div
          style={
            props.hideContentDivider
              ? { paddingTop: 18, paddingBottom: 8 }
              : {
                  marginTop: 12,
                  paddingTop: 12,
                  borderTop: "1px solid rgba(255,255,255,0.2)",
                }
          }
        >
          {props.children}
        </div>
      ) : "actionText" in props ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            marginTop: 12,
            paddingTop: 12,
            borderTop: "1px solid rgba(255,255,255,0.2)",
          }}
        >
          <span
            className="text-sm font-semibold"
            style={{ color: colors.primary }}
          >
            {props.actionText}
          </span>
          <ArrowRight size={16} color={colors.primary} />
        </div>
      ) : null}
    </div>
  );

  if (isPressable) {
    return (
      <button
        type="button"
        onClick={props.onPress}
        style={{
          display: "block",
          width: "100%",
          textAlign: "left",
          background: "none",
          border: "none",
          padding: 0,
          cursor: "pointer",
        }}
      >
        {content}
      </button>
    );
  }
  return content;
}

export default StatCard;
